// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the two ways Solar System mode lays the bodies out in the room
 * (owner, 2026-11-04; docs/AYNI_XR_SOLAR_SYSTEM_PLAN.md). Metres, in the
 * J2000 ecliptic frame with the Sun at the origin (the scene turns them
 * into its own axes).
 *
 * - `distances` (the start, "Pluto fits"): true distances, one scale for
 *   all of them, so that Pluto's farthest point is 2.5 m from the Sun and
 *   the whole system is 5 m across. One astronomical unit is then 5 cm. At
 *   that scale the Sun is half a millimetre wide and the Earth a few
 *   thousandths of one, so the bodies are drawn as markers of a fixed size
 *   on the eye, and the orbits as lines.
 * - `sizes`: true sizes, one scale for all of them (the Sun 0.9 m across,
 *   the Earth 8 mm), with the distances squeezed (each body keeps its
 *   direction from the Sun; its distance becomes the Sun's radius plus a
 *   square root of the true one) so the system still fits in 5 m.
 */

import { AU_KM, type Vec3 } from './ephemeris'

export type OrreryLayout = 'distances' | 'sizes'

/** The room's radius: Pluto's farthest point sits this far from the Sun, metres. */
export const ROOM_RADIUS_M = 2.5

/** Pluto's aphelion, AU (Standish Table 1 at J2000: a (1 + e)). */
export const PLUTO_APHELION_AU = 39.48211675 * (1 + 0.2488273)

/** True distances: metres per astronomical unit (about 5 cm). */
export const M_PER_AU = ROOM_RADIUS_M / PLUTO_APHELION_AU

/** True sizes: the Sun's radius in the room, metres. */
export const SIZES_SUN_RADIUS_M = 0.45
/** True sizes: metres per km of a body's radius (the Sun's 695,700 km → 0.45 m). */
export const SIZES_M_PER_KM = SIZES_SUN_RADIUS_M / 695_700
/** True sizes: the squeeze, metres per √AU, so Pluto's aphelion lands on the room's radius. */
export const SIZES_K = (ROOM_RADIUS_M - SIZES_SUN_RADIUS_M) / Math.sqrt(PLUTO_APHELION_AU)
/** True sizes: the Moon's distance from the Earth in the room (its true one would be 0.25 m), metres. */
export const SIZES_MOON_GAP_M = 0.025

function scale(v: Vec3, k: number): Vec3 {
  return [v[0] * k, v[1] * k, v[2] * k]
}

/** A heliocentric point (AU) where a layout puts it, metres. */
export function layoutPoint(layout: OrreryLayout, helioAu: Vec3): Vec3 {
  if (layout === 'distances') return scale(helioAu, M_PER_AU)
  const r = Math.hypot(helioAu[0], helioAu[1], helioAu[2])
  if (r === 0) return [0, 0, 0]
  return scale(helioAu, (SIZES_SUN_RADIUS_M + SIZES_K * Math.sqrt(r)) / r)
}

/**
 * Where a layout puts a body, metres from the Sun. The Moon goes by the
 * Earth: at true distances 0.13 mm from it, in `sizes` a fixed gap in its
 * true direction (`moonFromEarthAu` is the Moon's geocentric vector).
 */
export function layoutBodyPoint(layout: OrreryLayout, body: string, helioAu: Vec3, earthHelioAu: Vec3, moonFromEarthAu: Vec3): Vec3 {
  if (body !== 'moon') return layoutPoint(layout, helioAu)
  const earth = layoutPoint(layout, earthHelioAu)
  if (layout === 'distances') return [earth[0] + moonFromEarthAu[0] * M_PER_AU, earth[1] + moonFromEarthAu[1] * M_PER_AU, earth[2] + moonFromEarthAu[2] * M_PER_AU]
  const d = Math.hypot(moonFromEarthAu[0], moonFromEarthAu[1], moonFromEarthAu[2]) || 1
  return [earth[0] + (moonFromEarthAu[0] / d) * SIZES_MOON_GAP_M, earth[1] + (moonFromEarthAu[1] / d) * SIZES_MOON_GAP_M, earth[2] + (moonFromEarthAu[2] / d) * SIZES_MOON_GAP_M]
}

/** A body's radius in a layout, metres (true at that layout's scale). */
export function layoutRadius(layout: OrreryLayout, radiusKm: number): number {
  return layout === 'distances' ? (radiusKm / AU_KM) * M_PER_AU : radiusKm * SIZES_M_PER_KM
}
